// src/components/Reports.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || "http://localhost:3004";

// Komponent Tooltip
const Tooltip = ({ children, text, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(position);
  const tooltipRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const updatePosition = () => {
    if (!tooltipRef.current || !containerRef.current) return;
    
    const tooltip = tooltipRef.current;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let newPosition = position;
    
    // Sprawdź czy tooltip wychodzi poza prawą krawędź
    if (rect.left + tooltipRect.width > viewportWidth - 20) {
      newPosition = 'left';
    }
    // Sprawdź czy tooltip wychodzi poza lewą krawędź
    else if (rect.left - tooltipRect.width < 20) {
      newPosition = 'right';
    }
    // Sprawdź czy tooltip wychodzi poza górną krawędź
    else if (rect.top - tooltipRect.height < 20) {
      newPosition = 'bottom';
    }
    // Sprawdź czy tooltip wychodzi poza dolną krawędź
    else if (rect.bottom + tooltipRect.height > viewportHeight - 20) {
      newPosition = 'top';
    }
    
    setTooltipPosition(newPosition);
  };

  const handleMouseEnter = () => {
    setIsVisible(true);
    setTimeout(updatePosition, 10); // Delay dla renderowania
  };

  const positionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2'
  };

  return (
    <div 
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div 
          ref={tooltipRef}
          className={`absolute z-50 px-4 py-3 text-sm text-white bg-gray-900 rounded-lg shadow-xl max-w-sm min-w-max transition-opacity duration-200 opacity-100 ${positionClasses[tooltipPosition]}`}
          style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))' }}
        >
          <div className="whitespace-normal break-words leading-relaxed">
            {text}
          </div>
          <div className={`absolute w-2 h-2 bg-gray-900 transform rotate-45 ${
            tooltipPosition === 'top' ? 'top-full left-1/2 -translate-x-1/2 -mt-1' :
            tooltipPosition === 'bottom' ? 'bottom-full left-1/2 -translate-x-1/2 -mb-1' :
            tooltipPosition === 'left' ? 'left-full top-1/2 -translate-y-1/2 -ml-1' :
            'right-full top-1/2 -translate-y-1/2 -mr-1'
          }`}></div>
        </div>
      )}
    </div>
  );
};

const Reports = ({ token, currentUser }) => {
  const [activeTab, setActiveTab] = useState('sales');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  
  // Dane raportów
  const [salesReport, setSalesReport] = useState(null);
  const [utilizationReport, setUtilizationReport] = useState(null);
  const [financialReport, setFinancialReport] = useState(null);

  // Pobranie raportu sprzedaży
  const fetchSalesReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/reports/sales`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          groupBy: 'day'
        }
      });
      setSalesReport(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Błąd podczas pobierania raportu sprzedaży');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange.startDate, dateRange.endDate]);

  // Pobranie raportu wykorzystania
  const fetchUtilizationReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/reports/utilization`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });
      setUtilizationReport(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Błąd podczas pobierania raportu wykorzystania');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange.startDate, dateRange.endDate]);

  // Pobranie raportu finansowego
  const fetchFinancialReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/reports/financial`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });
      setFinancialReport(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Błąd podczas pobierania raportu finansowego');
    } finally {
      setLoading(false);
    }
  }, [token, dateRange.startDate, dateRange.endDate]);

  // Eksport raportu
  const exportReport = async (type) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${API_URL}/reports/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          type,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        },
        responseType: 'blob'
      });

      // Sprawdź czy odpowiedź to błąd JSON zamiast CSV
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || 'Błąd podczas eksportu');
      }

      // Pobieranie pliku
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `raport_${type}_${dateRange.startDate}_${dateRange.endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.error || err.message || 'Błąd podczas eksportu raportu');
    } finally {
      setLoading(false);
    }
  };

  // Wczytanie danych przy zmianie zakresu dat
  useEffect(() => {
    if (activeTab === 'sales') {
      fetchSalesReport();
    } else if (activeTab === 'utilization') {
      fetchUtilizationReport();
    } else if (activeTab === 'financial') {
      fetchFinancialReport();
    }
  }, [dateRange, activeTab, fetchSalesReport, fetchUtilizationReport, fetchFinancialReport]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Raporty sprzedaży i wykorzystania</h2>
        
        {/* Filtr dat */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Data od</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Data do</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Panel informacyjny */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-800 mb-1">Jak czytać raporty</h4>
              <p className="text-xs sm:text-sm text-blue-700">
                <strong>Sprzedaż:</strong> Pokazuje statystyki sprzedanych voucherów. 
                <strong> Wykorzystanie:</strong> Analizuje jak klienci używają voucherów. 
                <strong> Finanse:</strong> Porównuje przychody ze sprzedaży z kosztami wykorzystania.
                Najedź na ikony ℹ️ przy statystykach aby zobaczyć szczegółowe wyjaśnienia.
              </p>
            </div>
          </div>
        </div>

        {/* Zakładki */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            {[
              { id: 'sales', label: '📊 Sprzedaż', icon: '📊' },
              { id: 'utilization', label: '🎯 Wykorzystanie', icon: '🎯' },
              { id: 'financial', label: '💰 Finanse', icon: '💰' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Raport sprzedaży */}
      {activeTab === 'sales' && salesReport && (
        <div className="space-y-4 sm:space-y-6">
          {/* Statystyki ogólne */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="bg-white rounded-lg shadow p-3 sm:p-6">
              <Tooltip text="Całkowita liczba voucherów sprzedanych w wybranym okresie">
                <div className="text-xs sm:text-sm font-medium text-gray-500 cursor-help flex items-center">
                  Sprzedane vouchery
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Tooltip>
              <div className="text-lg sm:text-2xl font-bold text-gray-900">{salesReport.summary?.totalVouchers || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-6">
              <Tooltip text="Łączna wartość wszystkich sprzedanych voucherów (przed podatkami)">
                <div className="text-xs sm:text-sm font-medium text-gray-500 cursor-help flex items-center">
                  Łączny przychód
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Tooltip>
              <div className="text-lg sm:text-2xl font-bold text-green-600">{formatCurrency(salesReport.summary?.totalRevenue || 0)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-6">
              <Tooltip text="Liczba voucherów które zostały już wykorzystane przez klientów">
                <div className="text-xs sm:text-sm font-medium text-gray-500 cursor-help flex items-center">
                  Wykorzystane
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Tooltip>
              <div className="text-lg sm:text-2xl font-bold text-blue-600">{salesReport.summary?.usedVouchers || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-6">
              <Tooltip text="Liczba voucherów które są nadal aktywne i oczekują na wykorzystanie">
                <div className="text-xs sm:text-sm font-medium text-gray-500 cursor-help flex items-center">
                  Aktywne
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Tooltip>
              <div className="text-lg sm:text-2xl font-bold text-yellow-600">{salesReport.summary?.activeVouchers || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-3 sm:p-6 col-span-2 sm:col-span-1">
              <Tooltip text="Procent wykorzystanych voucherów. Wyższy wskaźnik = lepsze zaangażowanie klientów">
                <div className="text-xs sm:text-sm font-medium text-gray-500 cursor-help flex items-center">
                  Współczynnik wykorzystania
                  <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Tooltip>
              <div className="text-lg sm:text-2xl font-bold text-purple-600">{salesReport.summary?.utilizationRate || 0}%</div>
            </div>
          </div>

          {/* Sprzedaż według produktów */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Sprzedaż według produktów</h3>
              <button
                onClick={() => exportReport('sales')}
                className="w-full sm:w-auto bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
              >
                📥 Eksportuj CSV
              </button>
            </div>
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <Tooltip text="Nazwa produktu lub usługi dla której został wykupiony voucher">
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-help">
                            Produkt
                            <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </th>
                        </Tooltip>
                        <Tooltip text="Liczba sprzedanych voucherów dla tego produktu w wybranym okresie">
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-help">
                            Ilość
                            <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </th>
                        </Tooltip>
                        <Tooltip text="Łączna wartość sprzedaży dla tego produktu (ilość × cena jednostkowa)">
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-help">
                            Przychód
                            <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </th>
                        </Tooltip>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {salesReport.salesByProduct && salesReport.salesByProduct.map((product, index) => (
                        <tr key={index}>
                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">{product.product_name}</td>
                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">{product.quantity}</td>
                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm font-medium text-green-600">
                            {formatCurrency(product.revenue)}
                          </td>
                        </tr>
                      ))}
                      {(!salesReport.salesByProduct || salesReport.salesByProduct.length === 0) && (
                        <tr>
                          <td colSpan="3" className="px-3 sm:px-6 py-4 text-center text-gray-500 text-xs sm:text-sm">
                            Brak danych do wyświetlenia
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Raport wykorzystania */}
      {activeTab === 'utilization' && utilizationReport && (
        <div className="space-y-6">
          {/* Statystyki wykorzystania */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <Tooltip text="Średnia liczba dni od zakupu vouchera do jego wykorzystania. Niższa wartość oznacza szybsze wykorzystanie.">
                <div className="text-sm font-medium text-gray-500 cursor-help flex items-center">
                  Średni czas do wykorzystania
                  <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Tooltip>
              <div className="text-2xl font-bold text-gray-900">
                {utilizationReport.avgTimeToUse && typeof utilizationReport.avgTimeToUse === 'number' 
                  ? utilizationReport.avgTimeToUse.toFixed(1) 
                  : utilizationReport.avgTimeToUse || '0'} dni
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <Tooltip text="Rozkład voucherów według statusu: active (nieużywane), used (wykorzystane), expired (wygasłe), inactive (wyłączone)">
                <div className="text-sm font-medium text-gray-500 cursor-help flex items-center">
                  Status voucherów
                  <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Tooltip>
              <div className="space-y-2 mt-2">
                {utilizationReport.utilizationStats && utilizationReport.utilizationStats.map((stat, index) => (
                  <div key={index} className="flex justify-between">
                    <span className="text-sm text-gray-600 capitalize">{stat.status}</span>
                    <span className="text-sm font-medium">{stat.count} ({formatCurrency(stat.total_value)})</span>
                  </div>
                ))}
                {(!utilizationReport.utilizationStats || utilizationReport.utilizationStats.length === 0) && (
                  <div className="text-sm text-gray-500">Brak danych</div>
                )}
              </div>
            </div>
          </div>

          {/* Top klienci */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Top 10 klientów (wykorzystanie)</h3>
              <button
                onClick={() => exportReport('utilization')}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
              >
                📥 Eksportuj CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <Tooltip text="Imię i nazwisko klienta który kupił i wykorzystał vouchery">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-help">
                        Klient
                        <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </th>
                    </Tooltip>
                    <Tooltip text="Adres email klienta do kontaktu i marketingu">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-help">
                        Email
                        <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </th>
                    </Tooltip>
                    <Tooltip text="Liczba voucherów które klient faktycznie wykorzystał">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-help">
                        Wykorzystane
                        <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </th>
                    </Tooltip>
                    <Tooltip text="Łączna wartość wykorzystanych voucherów przez tego klienta">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-help">
                        Wartość
                        <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </th>
                    </Tooltip>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {utilizationReport.topCustomers && utilizationReport.topCustomers.map((customer, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.full_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{customer.vouchers_used}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        {formatCurrency(customer.total_value)}
                      </td>
                    </tr>
                  ))}
                  {(!utilizationReport.topCustomers || utilizationReport.topCustomers.length === 0) && (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                        Brak danych do wyświetlenia
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Raport finansowy */}
      {activeTab === 'financial' && financialReport && (
        <div className="space-y-6">
          {/* Podsumowanie finansowe */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <Tooltip text="Całkowita wartość wszystkich sprzedanych voucherów w wybranym okresie">
                <div className="text-sm font-medium text-gray-500 cursor-help flex items-center">
                  Łączny przychód
                  <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Tooltip>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(financialReport.summary?.totalRevenue || 0)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <Tooltip text="Wartość voucherów które zostały już wykorzystane - realny przepływ gotówki">
                <div className="text-sm font-medium text-gray-500 cursor-help flex items-center">
                  Wykorzystany przychód
                  <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Tooltip>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(financialReport.summary?.totalUtilized || 0)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <Tooltip text="Wartość aktywnych voucherów - zobowiązania firmy wobec klientów">
                <div className="text-sm font-medium text-gray-500 cursor-help flex items-center">
                  Zaległości
                  <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Tooltip>
              <div className="text-2xl font-bold text-yellow-600">{formatCurrency(financialReport.summary?.totalOutstanding || 0)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <Tooltip text="Wartość wygasłych voucherów - zysk bez świadczenia usług">
                <div className="text-sm font-medium text-gray-500 cursor-help flex items-center">
                  Stracone przychody
                  <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </Tooltip>
              <div className="text-2xl font-bold text-red-600">{formatCurrency(financialReport.summary?.totalLost || 0)}</div>
            </div>
          </div>

          {/* Porównanie przychodów */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Przychody vs wykorzystanie (miesięcznie)</h3>
              <button
                onClick={() => exportReport('financial')}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
              >
                📥 Eksportuj CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <Tooltip text="Miesiąc i rok dla którego są przedstawione dane finansowe">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-help">
                        Miesiąc
                        <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </th>
                    </Tooltip>
                    <Tooltip text="Łączna wartość voucherów sprzedanych w tym miesiącu - przychód od klientów">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-help">
                        Przychód ze sprzedaży
                        <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </th>
                    </Tooltip>
                    <Tooltip text="Wartość voucherów wykorzystanych w tym miesiącu - koszt świadczonych usług">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-help">
                        Wykorzystany przychód
                        <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </th>
                    </Tooltip>
                    <Tooltip text="Różnica między sprzedażą a wykorzystaniem. Dodatnia = zysk, ujemna = większe wykorzystanie">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-help">
                        Różnica
                        <svg className="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </th>
                    </Tooltip>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {financialReport.revenue && financialReport.revenue.length > 0 ? financialReport.revenue.map((month, index) => {
                    const utilized = financialReport.utilizedRevenue ? financialReport.utilizedRevenue.find(u => u.month === month.month) : null;
                    const monthRevenue = parseFloat(month.revenue) || 0;
                    const utilizedRevenue = parseFloat(utilized?.utilized_revenue) || 0;
                    const difference = monthRevenue - utilizedRevenue;
                    
                    return (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{month.month || 'Brak daty'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                          {formatCurrency(monthRevenue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                          {formatCurrency(utilizedRevenue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-yellow-600">
                          {formatCurrency(difference)}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                        Brak danych do wyświetlenia
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
